import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, FormControl, FormControlLabel, Radio, RadioGroup, TextField } from "@material-ui/core";

export const MULTICHOICE_OTHER_KEY = "__other__";
export const MULTICHOICE_OTHER_TEXT_MAX = 1000;

const sortOptions = (options = []) => {
  return [...options].sort((a, b) => {
    const aOrder = Number.isFinite(Number(a?.order)) ? Number(a.order) : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b?.order)) ? Number(b.order) : Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a?.label || "").localeCompare(String(b?.label || ""));
  });
};

const MultiChoiceQuestion = ({
  options,
  selectedOptionKey,
  onChange,
  allowOther,
  otherText,
  onOtherTextChange,
  otherLabel,
  otherPlaceholder,
  canClear,
  clearLabel,
}) => {
  const sortedOptions = sortOptions(options);
  const isOtherSelected = allowOther && selectedOptionKey === MULTICHOICE_OTHER_KEY;
  const otherInputRef = useRef(null);

  useEffect(() => {
    if (isOtherSelected && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, [isOtherSelected]);

  return (
    <FormControl component="fieldset" fullWidth>
      <RadioGroup
        value={selectedOptionKey}
        onChange={(event) => onChange(event.target.value)}
      >
        {sortedOptions.map((option) => (
          <FormControlLabel
            key={`${option.key}-${option.label}`}
            value={String(option.key)}
            control={<Radio color="primary" />}
            label={option.label || String(option.key)}
          />
        ))}
        {allowOther && (
          <FormControlLabel
            value={MULTICHOICE_OTHER_KEY}
            control={<Radio color="primary" />}
            label={otherLabel}
          />
        )}
      </RadioGroup>
      {isOtherSelected && (
        <TextField
          inputRef={otherInputRef}
          value={otherText}
          onChange={(event) => onOtherTextChange(event.target.value.slice(0, MULTICHOICE_OTHER_TEXT_MAX))}
          placeholder={otherPlaceholder}
          inputProps={{ maxLength: MULTICHOICE_OTHER_TEXT_MAX, "aria-label": otherLabel }}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          style={{ marginTop: "8px" }}
        />
      )}
      {canClear && selectedOptionKey && (
        <div style={{ marginTop: "8px" }}>
          <Button size="small" onClick={() => onChange("")}>
            {clearLabel}
          </Button>
        </div>
      )}
    </FormControl>
  );
};

MultiChoiceQuestion.propTypes = {
  options: PropTypes.array.isRequired,
  selectedOptionKey: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  allowOther: PropTypes.bool,
  otherText: PropTypes.string,
  onOtherTextChange: PropTypes.func,
  otherLabel: PropTypes.string,
  otherPlaceholder: PropTypes.string,
  canClear: PropTypes.bool,
  clearLabel: PropTypes.string,
};

MultiChoiceQuestion.defaultProps = {
  selectedOptionKey: "",
  allowOther: false,
  otherText: "",
  onOtherTextChange: () => {},
  otherLabel: "Other (please specify)",
  otherPlaceholder: "Please specify",
  canClear: false,
  clearLabel: "Clear selection",
};

export default MultiChoiceQuestion;
